/**
 * Grand-prize / multi-deal progress from offer.progress_tracking + deal counts.
 */

const initialOverall = () => ({ redeemed: 0, total: 0 });

const normalizeDealIds = raw => {
  if (!Array.isArray(raw) || !raw.length) return [];
  return raw
    .map(id => Number(id))
    .filter(id => !Number.isNaN(id));
};

const normalizeRedemptionTypes = raw => {
  if (!Array.isArray(raw) || !raw.length) return [];
  return raw.filter(t => typeof t === 'string' && t.length > 0);
};

// Activity TYPE ids (parent product templates) to count. Empty = count every
// activity in the tracked passes. The backend resolves these to the matching
// products and reports filtered per-deal counts, so the client trusts those
// counts when a type filter is active (it can't resolve template parents).
const normalizeIds = raw => {
  if (!Array.isArray(raw) || !raw.length) return [];
  return raw.map(id => Number(id)).filter(id => !Number.isNaN(id));
};

const getProgressConfigFromOffer = offer => {
  const raw = offer?.progress_tracking ?? offer?.progressTracking;
  if (!raw) return null;

  const dealIds = normalizeDealIds(raw.deal_ids ?? raw.dealIds);
  if (!dealIds.length) return null;

  const texts = raw.texts ?? {};
  const snapshot = raw.tracked_deals_snapshot ?? raw.trackedDealsSnapshot ?? [];
  return {
    deal_ids: dealIds,
    activity_type_ids: normalizeIds(raw.activity_type_ids ?? raw.activityTypeIds),
    redemption_types: normalizeRedemptionTypes(
      raw.redemption_types ?? raw.redemptionTypes
    ),
    display_format: raw.display_format ?? raw.displayFormat ?? 'fraction',
    tracked_deals_snapshot: Array.isArray(snapshot) ? snapshot : [],
    texts: {
      prize_name: texts.prize_name ?? texts.prizeName ?? '',
      activities_label:
        texts.activities_label ?? texts.activitiesLabel ?? 'Actions completed',
      description: texts.description ?? '',
      subtitle: texts.subtitle ?? ''
    }
  };
};

function matchesRedemptionType(op, redemptionTypes) {
  if (!redemptionTypes || !redemptionTypes.length) return true;
  const type =
    op?.redemptions ?? op?.product?.redemptions ?? op?.product?.redemption_type;
  return redemptionTypes.includes(type);
}

function countsFromOrderProducts(ops, redemptionTypes = []) {
  const filtered = ops.filter(op => matchesRedemptionType(op, redemptionTypes));
  const isRedeemed = op =>
    op.redeemed != null && op.redeemed !== '';
  const redeemed = filtered.filter(isRedeemed).length;
  const total = filtered.length;
  return { redeemed, total };
}

function getDealCounts(deal, rootGetters, redemptionTypes = [], filterActive = false) {
  const rApi = deal.progress_redeemed_count ?? deal.progressRedeemedCount;
  const tApi = deal.progress_total_count ?? deal.progressTotalCount;
  const apiTotal =
    tApi !== undefined && tApi !== null
      ? Math.max(0, Number(tApi) || 0)
      : null;
  const apiRedeemed =
    rApi !== undefined && rApi !== null
      ? Math.max(0, Number(rApi) || 0)
      : null;

  // When an activity-type filter is active, the per-deal numerator/denominator
  // are resolved server-side (the client can't map products to their parent
  // template). Trust the backend counts and skip the local optimistic recompute.
  if (filterActive) {
    return { redeemed: apiRedeemed ?? 0, total: apiTotal ?? 0 };
  }

  const ops = rootGetters['orderProducts/byDealId'](deal.id) || [];

  // Local OPs may be a partial slice (e.g. only the current venue's OPs are
  // loaded on a venue/check-in page). Use them only when they cover at least
  // the API-reported total; otherwise the backend counts are authoritative.
  // Within that path, take max(localRedeemed, apiRedeemed) so a fresh local
  // redemption still bumps the count optimistically.
  const localCounts = countsFromOrderProducts(ops, redemptionTypes);

  if (apiTotal !== null) {
    if (localCounts.total >= apiTotal) {
      return {
        redeemed: Math.max(localCounts.redeemed, apiRedeemed ?? 0),
        total: localCounts.total
      };
    }
    return {
      redeemed: Math.max(apiRedeemed ?? 0, localCounts.redeemed),
      total: apiTotal
    };
  }

  if (localCounts.total > 0) {
    return localCounts;
  }

  return { redeemed: 0, total: 0 };
}

function isOwnedDeal(deal, rootGetters) {
  const hasOrderDeals = Array.isArray(deal.order_deals) && deal.order_deals.length > 0;
  if (hasOrderDeals) return true;

  const ops = rootGetters['orderProducts/byDealId'](deal.id);
  return Array.isArray(ops) && ops.length > 0;
}

function isOrderDealExpired(orderDeal) {
  if (!orderDeal) return false;
  const completedAt = orderDeal.completed_at ?? orderDeal.completedAt;
  return completedAt != null && completedAt !== '';
}

function isDealExpired(deal) {
  if (!Array.isArray(deal?.order_deals) || !deal.order_deals.length) return false;
  return deal.order_deals.every(isOrderDealExpired);
}

/**
 * Live path: expiration from order_deals.completed_at only.
 * If order_deals were stripped (e.g. 14 day filter) but the deal is still owned,
 * fall back to API snapshot isExpired for the tracker row only.
 */
function resolveTrackedDealIsExpired(deal, snapshotEntry) {
  const ods = deal?.order_deals;
  if (Array.isArray(ods) && ods.length > 0) {
    return isDealExpired(deal);
  }
  if (snapshotEntry) {
    return !!snapshotEntry.isExpired;
  }
  return false;
}

function indexSnapshot(config) {
  const snapshot = config.tracked_deals_snapshot || [];
  const map = new Map();
  for (const entry of snapshot) {
    map.set(Number(entry.id), entry);
  }
  return map;
}

function buildSnapshot(config, rootGetters) {
  const trackedDeals = [];
  let redeemed = 0;
  let total = 0;
  const snapshotById = indexSnapshot(config);
  const filterActive = (config.activity_type_ids?.length ?? 0) > 0;

  for (const id of config.deal_ids) {
    const deal = rootGetters['deals/find'](id);
    const inStore = !!deal && isOwnedDeal(deal, rootGetters);

    if (inStore) {
      const counts = getDealCounts(deal, rootGetters, config.redemption_types, filterActive);
      // Hide passes with no tracked activities — a "0 / 0" row is meaningless.
      if (counts.total === 0) continue;
      const snapshotEntry = snapshotById.get(Number(id));
      trackedDeals.push({
        id: deal.id,
        title: deal.name,
        redeemed: counts.redeemed,
        total: counts.total,
        isExpired: resolveTrackedDealIsExpired(deal, snapshotEntry)
      });
      redeemed += counts.redeemed;
      total += counts.total;
      continue;
    }

    const entry = snapshotById.get(Number(id));
    if (!entry) continue;

    const entryRedeemed = Number(entry.redeemed) || 0;
    const entryTotal = Number(entry.total) || 0;
    // Hide passes with no tracked activities.
    if (entryTotal === 0) continue;
    trackedDeals.push({
      id: Number(entry.id),
      title: entry.name ?? '',
      redeemed: entryRedeemed,
      total: entryTotal,
      isExpired: !!entry.isExpired
    });
    redeemed += entryRedeemed;
    total += entryTotal;
  }

  trackedDeals.sort((a, b) => {
    if (a.isExpired !== b.isExpired) return a.isExpired ? 1 : -1;
    return String(a.title || '').localeCompare(String(b.title || ''), undefined, {
      sensitivity: 'base'
    });
  });

  return { trackedDeals, overallProgress: { redeemed, total } };
}

const state = () => ({
  progressTrackerConfig: null,
  trackedDeals: [],
  overallProgress: initialOverall()
});

const getters = {
  progressTrackerConfig: s => s.progressTrackerConfig,

  trackedDeals: s => s.trackedDeals,

  overallRedeemed: s => s.overallProgress.redeemed,

  overallTotal: s => s.overallProgress.total,

  progressPercentage: (_, getters) => {
    const total = getters.overallTotal;
    if (!total) return 0;
    const pct = (getters.overallRedeemed / total) * 100;
    return Math.min(100, Math.max(0, Math.round(pct * 10) / 10));
  },

  progressFraction: (_, getters) =>
    `${getters.overallRedeemed} / ${getters.overallTotal}`,

  isProgressTrackerVisible: state =>
    !!state.progressTrackerConfig && state.trackedDeals.length > 0
};

const mutations = {
  reset(state) {
    state.progressTrackerConfig = null;
    state.trackedDeals = [];
    state.overallProgress = initialOverall();
  },

  setFull(state, { config, trackedDeals, overallProgress }) {
    state.progressTrackerConfig = config;
    state.trackedDeals = trackedDeals;
    state.overallProgress = overallProgress;
  },

  setSnapshot(state, { trackedDeals, overallProgress }) {
    state.trackedDeals = trackedDeals;
    state.overallProgress = overallProgress;
  }
};

const actions = {
  initializeProgressTracker({ commit, rootState, rootGetters }) {
    const config = getProgressConfigFromOffer(rootState.offer);
    if (!config) {
      commit('reset');
      return;
    }

    const { trackedDeals, overallProgress } = buildSnapshot(
      config,
      rootGetters
    );

    if (!trackedDeals.length) {
      commit('reset');
      return;
    }

    commit('setFull', { config, trackedDeals, overallProgress });
  },

  recomputeProgress({ state, commit, rootGetters }) {
    const config = state.progressTrackerConfig;
    if (!config?.deal_ids?.length) return;

    const { trackedDeals, overallProgress } = buildSnapshot(
      config,
      rootGetters
    );

    if (!trackedDeals.length) {
      commit('reset');
      return;
    }

    commit('setSnapshot', { trackedDeals, overallProgress });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
